import React, { useEffect, useRef, useState, memo } from "react"

// Constants
const DEFAULT_POSTER =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=="
const INTERSECTION_THRESHOLD = [0, 0.5, 1.0]
const INTERSECTION_ROOT_MARGIN = "100px"
const RETRY_DELAYS = [250, 1000, 3000]

const HeroVideo = memo(({ inlinePosterDataURI, poster, src }) => {
  const [playing, setPlaying] = useState(false)
  const vref = useRef(null)
  const timers = useRef([])
  const isPlayingRef = useRef(false)

  useEffect(() => {
    const v = vref.current
    if (!v) return

    // Reset state
    isPlayingRef.current = false
    setPlaying(false)

    // Configure video properties
    v.muted = true
    v.defaultMuted = true
    v.playsInline = true
    v.setAttribute("playsinline", "")
    v.setAttribute("webkit-playsinline", "")
    v.setAttribute("muted", "")
    v.loop = true
    v.preload = "none"
    v.controls = false
    v.disablePictureInPicture = true
    v.disableRemotePlayback = true
    v.loading = "lazy"

    // Mark video as playing
    const mark = () => {
      if (!isPlayingRef.current) {
        isPlayingRef.current = true
        setPlaying(true)
        clearAll()
      }
    }

    // Attempt to play video
    const tryPlay = () => {
      if (isPlayingRef.current) return
      if (v.readyState === 0) {
        v.load()
      }
      v.play()
        .then(mark)
        .catch(() => {})
    }

    // Handle time update
    const onTimeupdate = () => {
      if (v.currentTime > 0 && !v.paused && !v.ended) mark()
    }

    // Handle visibility change
    const onVisibility = () => {
      if (document.visibilityState === "visible") tryPlay()
    }

    // Handle focus
    const onFocus = () => tryPlay()

    // Handle gesture
    const onGesture = () => tryPlay()

    // Schedule retry
    const schedule = ms => {
      const id = setTimeout(tryPlay, ms)
      timers.current.push(id)
    }

    // Clear all timers
    const clearAll = () => {
      timers.current.forEach(clearTimeout)
      timers.current = []
    }

    // Setup intersection observer
    let observer = null
    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
              tryPlay()
            } else if (!entry.isIntersecting) {
              v.pause()
              isPlayingRef.current = false
              setPlaying(false)
            }
          })
        },
        {
          threshold: INTERSECTION_THRESHOLD,
          rootMargin: INTERSECTION_ROOT_MARGIN,
        }
      )
      observer.observe(v)
    } else {
      // Fallback for browsers without IntersectionObserver
      v.load()
      tryPlay()
      RETRY_DELAYS.forEach(delay => schedule(delay))
    }

    // Setup event listeners
    v.addEventListener("playing", mark, { passive: true })
    v.addEventListener("timeupdate", onTimeupdate, { passive: true })
    v.addEventListener("canplay", tryPlay, { passive: true })
    v.addEventListener("loadeddata", tryPlay, { passive: true })
    document.addEventListener("visibilitychange", onVisibility, {
      passive: true,
    })
    window.addEventListener("focus", onFocus, { passive: true })
    window.addEventListener("pointerdown", onGesture, { passive: true })
    window.addEventListener("touchstart", onGesture, { passive: true })

    // Cleanup
    return () => {
      clearAll()
      if (observer) {
        observer.disconnect()
      }
      v.removeEventListener("playing", mark)
      v.removeEventListener("timeupdate", onTimeupdate)
      v.removeEventListener("canplay", tryPlay)
      v.removeEventListener("loadeddata", tryPlay)
      document.removeEventListener("visibilitychange", onVisibility)
      window.removeEventListener("focus", onFocus)
      window.removeEventListener("pointerdown", onGesture)
      window.removeEventListener("touchstart", onGesture)
    }
  }, [src])

  // Get background image
  const backgroundImage = inlinePosterDataURI || poster || DEFAULT_POSTER

  // Styles - keeping original structure that worked
  const abs = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "50% 50%",
    display: "block",
  }

  return (
    <section
      className="hero is-relative hero-video under-header"
      style={{
        background: `#0b0621 url('${backgroundImage}') center / cover no-repeat`,
      }}
    >
      <div style={{ position: "absolute", inset: 0 }}>
        <img
          src={poster}
          alt=""
          decoding="async"
          loading="eager"
          fetchpriority="high"
          style={{
            ...abs,
            zIndex: 10,
            opacity: playing ? 0 : 1,
            transition: "opacity .2s ease-out",
            pointerEvents: "none",
          }}
        />
        <video
          ref={vref}
          src={src}
          muted
          playsInline
          loop
          preload="none"
          loading="lazy"
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "50% 50%",
            display: "block",
            zIndex: 20,
            opacity: playing ? 1 : 0,
          }}
        />
      </div>

      <div className="hero-body" style={{ position: "relative", zIndex: 40 }}>
        <div className="container" style={{ alignContent: "center" }}>
          <div className="columns is-vcentered is-flex is-justify-content-center">
            <div className="column is-10-tablet is-9-desktop is-12-mobile has-text-centered">
              <h1
                className="title has-text-white has-text-centered has-text-weight-semibold is-size-1 is-size-3-mobile"
                style={{ marginBottom: 70 }}
              >
                Our mission is to transform advertising with AI that drives
                outcomes for brands and value for people
              </h1>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
})

HeroVideo.displayName = "HeroVideo"

export default HeroVideo
